import { SubdivisionFeature } from "@kb3d/models/classes/features/subdivision-feature";
import { SubmeshData } from "@kb3d/models/classes/features/submesh-feature";
import { SubmeshVertexData } from "@kb3d/view/helpers/geometry-helper";
import { FeatureGeometryProcessor, ProcessorData } from "./base-processor";

export class SubdivisionGeometryProcessor extends FeatureGeometryProcessor<SubdivisionFeature> {
    updateMeshGeometry(
        feature: SubdivisionFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<never>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);

        if (
            newVertexData.positions &&
            newVertexData.positions.length > 0 &&
            newVertexData.indices &&
            newVertexData.indices.length > 0
        ) {
            const level = feature.subdivisionLevel || 1;

            let positions = Array.from(newVertexData.positions);
            let normals = newVertexData.normals ? Array.from(newVertexData.normals) : [];
            let uvs = newVertexData.uvs ? Array.from(newVertexData.uvs) : [];
            let indices = Array.from(newVertexData.indices);

            for (let l = 0; l < level; l++) {
                const newPositions: number[] = [];
                const newNormals: number[] = [];
                const newUvs: number[] = [];
                const newIndices: number[] = [];

                const addVertex = (pos: number[], norm?: number[], uv?: number[]) => {
                    newPositions.push(...pos);
                    if (norm) newNormals.push(...norm);
                    if (uv) newUvs.push(...uv);
                    return (newPositions.length / 3) - 1;
                };

                for (let i = 0; i < indices.length; i += 3) {
                    const i0 = indices[i], i1 = indices[i + 1], i2 = indices[i + 2];

                    const v0 = positions.slice(i0 * 3, i0 * 3 + 3);
                    const v1 = positions.slice(i1 * 3, i1 * 3 + 3);
                    const v2 = positions.slice(i2 * 3, i2 * 3 + 3);

                    const n0 = normals.length ? normals.slice(i0 * 3, i0 * 3 + 3) : undefined;
                    const n1 = normals.length ? normals.slice(i1 * 3, i1 * 3 + 3) : undefined;
                    const n2 = normals.length ? normals.slice(i2 * 3, i2 * 3 + 3) : undefined;

                    const uv0 = uvs.length ? uvs.slice(i0 * 2, i0 * 2 + 2) : undefined;
                    const uv1 = uvs.length ? uvs.slice(i1 * 2, i1 * 2 + 2) : undefined;
                    const uv2 = uvs.length ? uvs.slice(i2 * 2, i2 * 2 + 2) : undefined;

                    const m01 = [(v0[0] + v1[0]) / 2, (v0[1] + v1[1]) / 2, (v0[2] + v1[2]) / 2];
                    const m12 = [(v1[0] + v2[0]) / 2, (v1[1] + v2[1]) / 2, (v1[2] + v2[2]) / 2];
                    const m20 = [(v2[0] + v0[0]) / 2, (v2[1] + v0[1]) / 2, (v2[2] + v0[2]) / 2];

                    const n01 = n0 && n1 ? [(n0[0] + n1[0]) / 2, (n0[1] + n1[1]) / 2, (n0[2] + n1[2]) / 2] : undefined;
                    const n12 = n1 && n2 ? [(n1[0] + n2[0]) / 2, (n1[1] + n2[1]) / 2, (n1[2] + n2[2]) / 2] : undefined;
                    const n20 = n2 && n0 ? [(n2[0] + n0[0]) / 2, (n2[1] + n0[1]) / 2, (n2[2] + n0[2]) / 2] : undefined;

                    const uv01 = uv0 && uv1 ? [(uv0[0] + uv1[0]) / 2, (uv0[1] + uv1[1]) / 2] : undefined;
                    const uv12 = uv1 && uv2 ? [(uv1[0] + uv2[0]) / 2, (uv1[1] + uv2[1]) / 2] : undefined;
                    const uv20 = uv2 && uv0 ? [(uv2[0] + uv0[0]) / 2, (uv2[1] + uv0[1]) / 2] : undefined;

                    const iV0 = addVertex(v0, n0, uv0);
                    const iV1 = addVertex(v1, n1, uv1);
                    const iV2 = addVertex(v2, n2, uv2);
                    const iM01 = addVertex(m01, n01, uv01);
                    const iM12 = addVertex(m12, n12, uv12);
                    const iM20 = addVertex(m20, n20, uv20);

                    newIndices.push(iV0, iM01, iM20);
                    newIndices.push(iM01, iV1, iM12);
                    newIndices.push(iM12, iV2, iM20);
                    newIndices.push(iM01, iM12, iM20);
                }

                positions = newPositions;
                normals = newNormals;
                uvs = newUvs;
                indices = newIndices;
            }

            newVertexData.positions = new Float32Array(positions);
            newVertexData.indices = new Int32Array(indices);
            if (normals.length) newVertexData.normals = new Float32Array(normals);
            if (uvs.length) newVertexData.uvs = new Float32Array(uvs);

            newVertexData.submeshData = vertexData.submeshData?.map(s => ({ ...s })) || [];
        }

        return newVertexData;
    }
}